import { Button, Tooltip } from '@blueprintjs/core';
import {
  lock_hooks,
  useApplicationDetail,
  useApplicationStatusUtils,
  useArchiveApplication,
  useLoanQuotes,
  useLoanRequests,
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import { useConfirmDialog } from '../../../hooks/utils/useConfirmDialog';
import {
  loan_application,
  loan_quote,
  loan_request,
  event_log,
} from '@axo/shared/data-access/types';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { EventCodeACE } from '../../EventLogWrapper/event-code';
import { loan_application_lender_tier_hooks } from '@axo/shared/data-access/hooks';

export function ArchiveButton({ applicationID }: { applicationID: string }) {
  const archiveApplication = useArchiveApplication(applicationID);
  const { confirm } = useConfirmDialog();
  const app = useApplicationDetail(applicationID);
  const { isFinalApplicationStatus } = useApplicationStatusUtils();
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    app.data?.MarketCountry
  ).data;
  const quotes = useLoanQuotes(applicationID);
  const { isInQuoteStatusGroup } = useQuoteStatusUtils();
  const requests = useLoanRequests({ loanApplicationID: applicationID });
  const { isInRequestStatusGroup } = useRequestStatusUtils();
  const appTag: event_log.Resource = {
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  };
  const log = useEventLogger(appTag);
  const lenderTier =
    loan_application_lender_tier_hooks.useLenderTier(applicationID);

  const handleArchive = () => {
    confirm({
      message: 'Are you sure you want to archive this application?',
      confirmText: 'Archive',
      cancelText: 'Cancel',
      confirmCallback: async () => {
        await archiveApplication.mutateAsync(
          loan_application.ArchiveRequestType.AutomaticEndStatus
        );
        log(EventCodeACE.ArchiveApplication);
      },
    });
  };

  const isFinalStatus = app.data?.Status
    ? isFinalApplicationStatus(app.data.Status)
    : false;

  const hasActiveLoanQuotes = quotes.data?.some((quote) =>
    isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Active)
  );

  const isPendingResponseFromBank = requests.data?.some((request) =>
    isInRequestStatusGroup(
      request.Status,
      loan_request.RequestStatusGroup.PendingRegistration
    )
  );

  const tierRegistrationOngoing = lenderTier.data
    ? lenderTier.data.Ongoing
    : false;

  function canArchive() {
    if (!hasLock) {
      return {
        disabled: true,
        text: 'You can only edit the application when you have locked it.',
      };
    }
    if (isFinalStatus) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when in a final state.',
      };
    }
    if (hasActiveLoanQuotes) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when there are active loan quotes.',
      };
    }
    if (isPendingResponseFromBank) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when pending a response from a bank.',
      };
    }
    if (tierRegistrationOngoing) {
      return {
        disabled: true,
        text: 'Applications cannot be archived when the tier registration is ongoing.',
      };
    }
    return {
      disabled: false,
      text: '',
    };
  }

  return (
    <Tooltip
      disabled={!canArchive().disabled}
      placement={'top'}
      content={<span>{canArchive().text}</span>}
      openOnTargetFocus={false}
    >
      <Button
        intent="none"
        icon="archive"
        disabled={canArchive().disabled}
        onClick={handleArchive}
        loading={archiveApplication.isLoading}
      >
        Archive application
      </Button>
    </Tooltip>
  );
}
